import {
    Component,
    Host,
    Prop,
    h,
    Watch,
    Element,
    Event,
    EventEmitter,
    Listen,
    State,
    Method,
} from "@stencil/core";
import api from "../../utils/api";

@Component({
    tag: "ux-iws-async-tab",
    styleUrl: "async-tab.scss",
    shadow: false,
})
export class Tab {
    @Element() domNode: HTMLUxIwsAsyncTabElement;

    /** Sets the name for the corresponding tab-button */
    @Prop({ reflect: true }) name: string = "";
    /** Sets ID for Tab */
    @Prop({ reflect: true }) tabId: string = "";
    /** the url from which the content of the tab needs to be fetched */
    @Prop() url: string;
    /** error message to be shown when the component encounters fetching problems */
    @Prop() errorMessage: string =
        "The data can not be retrieved at the moment. Please try again later.";
    /** if set, the content of the tab will reload every time that the tab is selected */
    @Prop() reloadOnActivation: boolean = false;

    /** Boolean to indicate active tab */
    @State() active: boolean = false;
    /** is loading state, for when the tab is fetching content */
    @State() isLoading: boolean = false;
    /** set to true if the content of the tab has loaded */
    @State() hasLoaded: boolean = false;
    /** has error */
    @State() hasError: boolean = false;

    /** event emitted when the async content of the tab has been loaded and inserted */
    @Event({
        eventName: "uxAsyncTabContentLoaded",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    asyncTabContentLoaded: EventEmitter;

    @Listen("uxActiveTabSet", { target: "document" })
    setActiveTab(event: CustomEvent) {
        if (
            this.domNode.closest("ux-tab-container") ===
            (event.target as HTMLElement)
        )
            this.active = event.detail === this.tabId;
    }

    @Listen("uxAsyncTabLoadingStart")
    setLoadingStart() {
        this.isLoading = true;
    }

    @Listen("uxAsyncTabLoadingEnd")
    setLoadingEnd() {
        this.isLoading = false;
    }

    @Listen("uxAsyncTabError")
    setError() {
        this.hasError = true;
    }

    @State() hasSliderInTab: boolean;

    async componentDidLoad() {
        if (this.active && !this.isLoading) await this.loadContent();
    }

    /** Reloads the content of the tab */
    @Method()
    async reload() {
        this.hasError = false;
        this.isLoading = true;
        this.domNode.innerHTML = "";
        await this.setTabContent();
    }

    @Watch("active")
    async loadContent() {
        if (!this.active) return;
        if (this.reloadOnActivation) {
            if (this.isLoading === true) return;
            await this.reload();
        } else {
            if (this.hasLoaded === true || this.isLoading == true) return;
            this.hasError = false;
            this.isLoading = true;
            await this.setTabContent();
        }
    }

    private setTabContent = async () => {
        try {
            if (isMockUrl(this.url))
                await new Promise((resolve) => setTimeout(resolve, 2000));
            const responseHTML = await api<string>(this.url);
            this.domNode.innerHTML = responseHTML;
            this.hasLoaded = true;
            this.asyncTabContentLoaded.emit({
                tabId: this.tabId,
            });
        } catch {
            this.hasError = true;
        }
        this.isLoading = false;
    };

    @Watch("hasLoaded")
    detectSliderInTab() {
        this.hasSliderInTab = this.domNode.querySelector("ux-slider") !== null;
    }

    render() {
        const { isLoading, hasError, errorMessage } = this;

        return (
            <Host
                class={{
                    ["ux-iws-async-tab"]: true,
                    ["is-active"]: this.active,
                    ["ux-iws-async-tab--with-slider"]: this.hasSliderInTab,
                }}
            >
                {hasError && (
                    <div class="ux-u-mt-xs ux-u-text-align--center">
                        <div class="ux-color-error">{errorMessage}</div>
                    </div>
                )}
                {isLoading && <ux-loader variant="spinner"></ux-loader>}
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return (
        url.indexOf("mock-data/") > -1 ||
        url.indexOf("Assets/fetchContent") > -1
    );
}
